import React from "react";
import PropTypes from "prop-types";
import {
  ComposedChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { formatNumber } from "@signolteam/shared/units-of-measure";
import {
  roundDownToNearestIncrement,
  roundUpToNearestIncrement,
} from "@src/constants/convertions";
import { AppUtils } from "@src/utils";
import { useManagerContext } from "@src/context/managerContext";
import { useDCContext } from "@src/context/dcContext";
import { getNumberFormatterForUnit } from "@src/components/utility/getNumberFormatted";
import CardContainer from "@src/components/cards/CardContainer";
import { Loader, TitleWithTooltip } from "@src/components/atoms";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";

const CustomTooltip = (props) => {
  const { active, payload, successChart } = props;
  const { getDynamicReplacement } = useDCContext();
  const { profile, selectedBehaviour } = useManagerContext();
  const { industry, preferredEventName, preferredEventNamePlural } =
    profile.customer;

  const unit = successChart ? "%" : "kg";
  const formatFunction = getNumberFormatterForUnit(unit, industry);
  if (active && payload && payload.length) {
    const details = payload[0].payload;

    const dcPrefix = successChart
      ? "fuel_manager__behaviour__success_section__success_per_operator_chart"
      : "fuel_manager__behaviour__fuel_saving_section__fuel_saving_per_operator_chart";
    const currentAvg = getDynamicReplacement(
      `${dcPrefix}_tooltip__current_avg`,
      {
        BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym,
      },
    );
    const preSignol = getDynamicReplacement(`${dcPrefix}_tooltip__pre_signol`, {
      BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym,
    });
    const opportunities = getDynamicReplacement(
      `${dcPrefix}_tooltip__opportunities`,
      {
        NUMBER: formatNumber(details.opportunities).toString(),
        EVENT_NAME_PLURAL: preferredEventNamePlural,
      },
    );
    const opportunity = getDynamicReplacement(
      `${dcPrefix}_tooltip__opportunity`,
      {
        NUMBER: formatNumber(details.opportunities).toString(),
        EVENT_NAME: preferredEventName,
      },
    );
    const textNoChange = getDynamicReplacement(
      `${dcPrefix}_tooltip__no_change`,
    );

    const successRateChangeFormatted = formatFunction(
      details.successRateChange,
      null,
      { includePositiveSign: true },
    );

    return (
      <div className="bg-white shadow p-2">
        <div className="p-1.5">
          <div className="font-bold text-lg">
            {successRateChangeFormatted.isZeroValue
              ? textNoChange
              : successRateChangeFormatted.toString()}
          </div>
          {!!details.opportunities && (
            <div className="text-sm text-neutral-500">
              {details.opportunities === 1 ? opportunity : opportunities}
            </div>
          )}
        </div>
        <div className="border-t border-neutral-500 p-2 text-neutral-500">
          <table>
            <tbody>
              <tr>
                <td>{formatFunction(details.values[0]).toString()}</td>
                <td className="pl-2">
                  <span className="text-sm">{preSignol}</span>
                </td>
              </tr>
              <tr>
                <td>{formatFunction(details.values[1]).toString()}</td>
                <td className="pl-2">
                  <span className="text-sm">{currentAvg}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  return null;
};

/** Also known as the Comparative S-Curve Chart (or just S-Curve) */
const BehaviourSuccessOrSavingsPerOperator = ({ data, isLoading = false }) => {
  const { getDynamicReplacement } = useDCContext();
  const { profile, selectedBehaviour, selectedBehaviourColour } =
    useManagerContext();
  const { industry, preferredEventName, preferredEventNamePlural } =
    profile.customer;

  const successChart = typeof data?.[0]?.successRateActual !== "undefined";
  const perOperatorChartKeyPostSignol = getDynamicReplacement(
    successChart
      ? "fuel_manager__behaviour__success_section__success_per_operator_chart_key_post_signol"
      : "fuel_manager__behaviour__fuel_saving_section__fuel_saving_per_operator_char_key_post_signol",
    {
      BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym,
    },
  );
  const perOperatorChartKeyPreSignol = getDynamicReplacement(
    successChart
      ? "fuel_manager__behaviour__success_section__success_per_operator_chart_key_pre_signol"
      : "fuel_manager__behaviour__fuel_saving_section__fuel_saving_per_operator_chart_key_pre_signol",
    {
      BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym,
    },
  );
  const perOperatorChartXAxisTitle = getDynamicReplacement(
    successChart
      ? "fuel_manager__behaviour__success_section__success_per_operator_x_axis_title"
      : "fuel_manager__behaviour__fuel_saving_section__fuel_saving_per_operator_chart_x_axis_title",
  );
  const successPerOperatorChartTitle = getDynamicReplacement(
    "fuel_manager__behaviour__success_section__success_per_operator_chart_title",
    {
      BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym,
    },
  );
  let highestValue = 0;
  let lowestValue = 0;

  data.forEach((operator) => {
    if (operator.avgNetFuelSavingKgPreSignol > highestValue) {
      highestValue = operator.avgNetFuelSavingKgPreSignol;
    }

    if (operator.avgNetFuelSavingKgPostSignol > highestValue) {
      highestValue = operator.avgNetFuelSavingKgPostSignol;
    }

    if (operator.avgNetFuelSavingKgPreSignol < lowestValue) {
      lowestValue = operator.avgNetFuelSavingKgPreSignol;
    }

    if (operator.avgNetFuelSavingKgPostSignol < lowestValue) {
      lowestValue = operator.avgNetFuelSavingKgPostSignol;
    }
  });

  const domainMin = successChart
    ? 0
    : lowestValue < -100
      ? roundDownToNearestIncrement(lowestValue, 100)
      : roundDownToNearestIncrement(lowestValue, 10);
  const domainMax = successChart
    ? 100
    : highestValue > 100
      ? roundUpToNearestIncrement(highestValue, 100)
      : roundUpToNearestIncrement(highestValue, 10);
  const behaviourColour = selectedBehaviourColour.codes["400"];
  const baselineColour = "#04454E";
  const formattedData = [];
  data.forEach((entry) => {
    formattedData.push({
      values: successChart
        ? [entry.successRatePreSignol, entry.successRateActual]
        : [
            entry.avgNetFuelSavingKgPreSignol,
            entry.avgNetFuelSavingKgPostSignol,
          ],
      baseline: successChart
        ? entry.successRatePreSignol
        : entry.avgNetFuelSavingKgPreSignol,
      opportunities: entry.countEventsPerformablePostSignol,
      successRateChange: parseFloat(
        (successChart
          ? entry.successRateActual - entry.successRatePreSignol
          : entry.avgNetFuelSavingKgPostSignol -
            entry.avgNetFuelSavingKgPreSignol
        ).toFixed(1),
      ),
      bvrAcronym: selectedBehaviour.behaviourAcronym,
    });
  });
  const ticks = successChart
    ? null
    : lowestValue < 0
      ? [
          domainMin,
          Math.floor(domainMin / 2),
          0,
          Math.floor(domainMax / 2),
          domainMax,
        ]
      : null;
  return (
    <CardContainer addaptivePadding allowOverflow>
      <TitleWithTooltip
        title={successPerOperatorChartTitle}
        centered={false}
        tooltipRightPosition={0}
      />
      {isLoading ? (
        <Loader />
      ) : (
        <div>
          <div className="flex flex-col md:flex-row">
            <div className="flex items-center text-sm mb-6 w-full md:w-1/2">
              <span
                className={`inline-block w-3 h-3 bg-${selectedBehaviourColour.name}-300 rounded-xl mr-2`}
              />
              {perOperatorChartKeyPostSignol}
            </div>
            <div className="flex items-center text-sm mb-6 w-full md:w-1/2">
              <span
                className={`inline-block w-3 h-3 bg-primary-900 rounded-xl mr-2`}
              />
              {perOperatorChartKeyPreSignol}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <ComposedChart
              data={formattedData}
              barGap={1}
              margin={{
                top: 5,
                right: 5,
                bottom: 5,
                left: successChart ? -20 : 0,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                fill="#F4F4F4"
                verticalCoordinatesGenerator={(props) => {
                  // how many data points do we want between each vertical line
                  const dataPointsPerVerticalLine = 6;
                  // work out the width of the chart note that the left offset accounts for the vertical axis and labels so that is our 0
                  const chartWidth = props.width - props.offset.left;
                  // work out the width of each data point
                  const dataPointWidth = chartWidth / props.xAxis.domain.length;
                  // work out the total number of lines we need
                  const numberOfVerticalLines = parseInt(
                    props.xAxis.domain.length / dataPointsPerVerticalLine,
                    10,
                  );
                  const verticalLinePositions = [];
                  for (let i = 0; i < numberOfVerticalLines; i++) {
                    // work out the position of each line using the left offset as 0
                    verticalLinePositions.push(
                      props.offset.left +
                        dataPointWidth * dataPointsPerVerticalLine * (i + 1),
                    );
                  }

                  return verticalLinePositions;
                }}
              />
              <XAxis dataKey="name" />
              <YAxis
                type="number"
                domain={[domainMin, domainMax]}
                tickCount={6}
                unit={successChart ? "%" : "kg"}
                tick={{ fill: "black", fontSize: "0.875rem" }}
                ticks={ticks}
              />
              <Tooltip
                allowEscapeViewBox={{ x: false, y: true }}
                content={
                  <CustomTooltip
                    preferredEventName={preferredEventName}
                    preferredEventNamePlural={preferredEventNamePlural}
                    successChart={successChart}
                    getDynamicReplacement={getDynamicReplacement}
                    industry={industry}
                  />
                }
                wrapperStyle={{ outline: "none" }}
              />
              <Bar dataKey="values" barSize={20} fill={behaviourColour} />
              <Line
                type="monotone"
                dot={false}
                dataKey="baseline"
                stroke={baselineColour}
                strokeWidth={3}
              />
              {lowestValue < 0 && (
                <ReferenceLine y={0} stroke="#000000" strokeDasharray="5 5" />
              )}
            </ComposedChart>
          </ResponsiveContainer>
          <div className="w-full text-center">{perOperatorChartXAxisTitle}</div>
        </div>
      )}
    </CardContainer>
  );
};

export const behaviourSuccessOrSavingsPerOperatorControls = {
  data: {
    propType: PropTypes.array.isRequired,
    description: "Array of operator data",
    controlType: "object",
    defaultValue: [],
  },
  isLoading: COMMON_CONTROLS.isLoading,
};

BehaviourSuccessOrSavingsPerOperator.propTypes =
  AppUtils.getPropTypeFromControls(
    behaviourSuccessOrSavingsPerOperatorControls,
  );

export default BehaviourSuccessOrSavingsPerOperator;
